import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { trpc } from '@/lib/trpc';

// ─── Item shape ───────────────────────────────────────────────────────────────
export interface BasketItem {
  id:    number;
  title: string;
  price: number; // 0 = free
}

// ─── Context shape — spec-exact function names ────────────────────────────────
interface BasketCtx {
  items:          BasketItem[];
  addItem:        (item: BasketItem) => void;
  removeItem:     (id: number) => void;
  clearBasket:    () => void;
  getItemCount:   () => number;
  getTotalPrice:  () => number;
}

const BasketContext = createContext<BasketCtx | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────
/**
 * Basket lives both in local React state and in the DB (when the user is
 * signed in). Anonymous users keep their basket client-only; on sign-in we
 * fetch the server basket and merge any local items into it.
 */
export function BasketProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<BasketItem[]>([]);
  const utils = trpc.useUtils();

  // Probe auth state so we know whether to sync.
  const { data: user } = trpc.auth.me.useQuery(undefined, {
    retry: false, staleTime: 60_000,
  });
  const userId = user?.id ?? null;

  // Server basket query — only enabled when authenticated
  const serverBasket = trpc.basket.list.useQuery(undefined, {
    enabled: !!userId, staleTime: 30_000,
  });

  // Mutations (call as fire-and-forget; we keep optimistic local state in sync)
  const addMut    = trpc.basket.add.useMutation();
  const removeMut = trpc.basket.remove.useMutation();
  const clearMut  = trpc.basket.clear.useMutation();

  // Refs so callbacks always see latest values without retriggering effects
  const itemsRef  = useRef(items);
  itemsRef.current = items;
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  // ── Sync on login / on basket-list update ─────────────────────────────────
  useEffect(() => {
    if (!userId || !serverBasket.data) return;

    const serverItems: BasketItem[] = serverBasket.data.map(b => ({
      id:    b.course.id,
      title: b.course.title,
      price: parseFloat(b.course.price as unknown as string) || 0,
    }));

    // Merge: any local items not on the server get pushed up; the resulting
    // unique set becomes the new local state.
    const serverIds = new Set(serverItems.map(i => i.id));
    const localOnly = itemsRef.current.filter(i => !serverIds.has(i.id));

    // Persist local-only items to the server (fire and forget)
    localOnly.forEach(i => {
      addMut.mutate({ courseId: i.id });
    });

    setItems([...serverItems, ...localOnly]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, serverBasket.data]);

  // ── API ────────────────────────────────────────────────────────────────────
  const addItem = useCallback((item: BasketItem) => {
    setItems(prev => prev.find(i => i.id === item.id) ? prev : [...prev, item]);
    if (userIdRef.current) {
      addMut.mutate({ courseId: item.id }, {
        onSuccess: () => utils.basket.list.invalidate(),
      });
    }
  }, [addMut, utils]);

  const removeItem = useCallback((id: number) => {
    setItems(prev => prev.filter(i => i.id !== id));
    if (userIdRef.current) {
      removeMut.mutate({ courseId: id }, {
        onSuccess: () => utils.basket.list.invalidate(),
      });
    }
  }, [removeMut, utils]);

  const clearBasket = useCallback(() => {
    setItems([]);
    if (userIdRef.current) {
      clearMut.mutate(undefined, {
        onSuccess: () => utils.basket.list.invalidate(),
      });
    }
  }, [clearMut, utils]);

  const getItemCount  = useCallback(() => items.length, [items]);
  const getTotalPrice = useCallback(
    () => items.reduce((sum, i) => sum + i.price, 0),
    [items],
  );

  return (
    <BasketContext.Provider
      value={{ items, addItem, removeItem, clearBasket, getItemCount, getTotalPrice }}
    >
      {children}
    </BasketContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useBasket() {
  const ctx = useContext(BasketContext);
  if (!ctx) throw new Error('useBasket must be used inside <BasketProvider>');
  return ctx;
}

/**
 * Convenience aliases — keep older imports working without changes.
 */
export const useCart      = useBasket;
export const CartProvider = BasketProvider;
export type  CartItem     = BasketItem;
